/** H5 参会端 PTT 权限：按 speakMode + session 快照 + floor 判定是否可发言（D2/D11） */

export type CrV3SpeakRole = 'host' | 'viewer'

export interface CrV3SpeakSessionPatch {
  speakMode?: string
  canParticipantSpeak?: boolean
  participant?: Array<{ id?: string; liveSpeech?: boolean }>
}

export interface CrV3ActiveSpeakerPatch {
  speaking?: boolean
  clientId?: string
}

/** D11：audio:busy 粘性位 OR activeSpeaker 推导（防 recompute 竞态清 block） */
export function computeFloorBlocked(
  forceFloorBlocked: boolean,
  activeSpeaker: CrV3ActiveSpeakerPatch,
  selfClientId: string,
  selfStreaming: boolean,
  speakMode?: string,
): boolean {
  return (
    forceFloorBlocked
    || resolveFloorBlocked(activeSpeaker, selfClientId, selfStreaming, speakMode)
  )
}

/** openDiscussion 下是否因他人占 floor 而禁 PTT（D11） */
export function resolveFloorBlocked(
  activeSpeaker: CrV3ActiveSpeakerPatch,
  selfClientId: string,
  selfStreaming: boolean,
  speakMode?: string,
): boolean {
  if ((speakMode || 'hostOnly') !== 'openDiscussion') {
    return false
  }
  if (!activeSpeaker.speaking) {
    return false
  }
  if (!activeSpeaker.clientId || activeSpeaker.clientId === selfClientId) {
    return false
  }
  if (selfStreaming) {
    return false
  }
  return true
}

/** 统一 PTT 门控：host / viewer × 三 speakMode（D2） */
export function resolvePttEnabled(
  role: CrV3SpeakRole,
  clientId: string,
  session: CrV3SpeakSessionPatch,
  floorBlocked: boolean,
): boolean {
  const mode = session.speakMode || 'hostOnly'
  if (mode === 'hostOnly') {
    return role === 'host'
  }
  if (mode === 'designated') {
    if (role === 'host') {
      return false
    }
    const self = (session.participant || []).find((p) => p.id === clientId)
    return Boolean(self?.liveSpeech)
  }
  if (mode === 'openDiscussion') {
    // cr-v3-open-discussion-v2-host D2：讨论模式仅 viewer 可 PTT，主持静默
    if (role === 'host') {
      return false
    }
    return Boolean(session.canParticipantSpeak) && !floorBlocked
  }
  return false
}

/** 参会 viewer 是否允许 PTT（不含 floor；与 backend _participant_authorized 对齐） */
export function resolveViewerSpeakAllowed(
  session: CrV3SpeakSessionPatch,
  clientId: string,
): boolean {
  return resolvePttEnabled('viewer', clientId, session, false)
}

/**
 * openDiscussion 下是否设置 forceFloorBlocked 粘性位。
 * 仅真实「他人占麦」粘住；token 超时/断线等不得永久锁死单人 PTT。
 */
export function shouldStickyForceFloorBlock(
  reason: string | undefined,
  speakMode?: string,
): boolean {
  if ((speakMode || 'hostOnly') !== 'openDiscussion') {
    return false
  }
  return reason === 'floor_occupied'
}
